import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileDown, Download, Layers, Users, Loader2, AlertCircle, Building2, Clock } from "lucide-react";
import HRLayout from "../components/HRLayout";
import { hrAuditHistory, hrDownloadPortfolioReport, hrDownloadAuditReport, formatApiError } from "../lib/api";

const fmtEuro = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
const fmtDateTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return `${d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" })} · ${d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}`;
};

export default function HRReports() {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [generatingPortfolio, setGeneratingPortfolio] = useState(false);
    const [regeneratingId, setRegeneratingId] = useState(null);

    const loadHistory = async () => {
        setLoading(true);
        setError(null);
        try {
            const h = await hrAuditHistory();
            setHistory(h);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { loadHistory(); }, []);

    const downloadBlob = (blob, filename) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const handlePortfolioReport = async () => {
        setGeneratingPortfolio(true);
        try {
            const blob = await hrDownloadPortfolioReport();
            downloadBlob(blob, `portfolio-report-${new Date().toISOString().slice(0, 10)}.pdf`);
            await loadHistory();
        } catch (err) {
            alert(formatApiError(err));
        } finally {
            setGeneratingPortfolio(false);
        }
    };

    const handleRegenerateAudit = async (log) => {
        if (!log.client_id) {
            await handlePortfolioReport();
            return;
        }
        setRegeneratingId(log.id);
        try {
            const blob = await hrDownloadAuditReport(log.client_id);
            const safe = (log.client_name || "cliente").replace(/[^a-zA-Z0-9_-]+/g, "-");
            downloadBlob(blob, `audit-report-${safe}-${new Date().toISOString().slice(0, 10)}.pdf`);
            await loadHistory();
        } catch (err) {
            alert(formatApiError(err));
        } finally {
            setRegeneratingId(null);
        }
    };

    const portfolioCount = history.filter((h) => h.report_type === "portfolio_consolidato").length;
    const auditCount = history.filter((h) => h.report_type === "audit_cliente").length;

    return (
        <HRLayout
            breadcrumbs={[{ label: "Report & Export" }]}
            title="Report & Export"
            subtitle="Centro di download dei report PDF generati — audit di singolo cliente e snapshot consolidato di portafoglio."
        >
            {/* Report Portafoglio Consolidato — hero card */}
            <div className="hr-card shadow-xs overflow-hidden" data-testid="hero-portfolio-report">
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
                    <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center">
                            <Layers size={22} className="text-amber-300" />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-amber-300 mb-1">Nuovo</div>
                            <h2 className="text-xl font-bold tracking-tight">Report Portafoglio Consolidato</h2>
                            <p className="text-sm text-slate-300 mt-1 max-w-xl">
                                Snapshot complessivo di tutti i clienti in un unico PDF: distribuzione rischio, totali petitum/fondo rischi, dettaglio per cliente.
                                Ideale per la revisione periodica dello studio.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handlePortfolioReport}
                        disabled={generatingPortfolio}
                        className="inline-flex items-center gap-2 h-10 px-5 bg-amber-500 hover:bg-amber-400 text-slate-900 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50"
                        data-testid="btn-generate-portfolio-report"
                    >
                        {generatingPortfolio ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />}
                        Genera PDF
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
                <div className="hr-card shadow-xs p-5">
                    <div className="flex items-center justify-between">
                        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                            <Building2 size={16} className="text-slate-600" />
                        </div>
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Audit cliente</div>
                    </div>
                    <div className="text-2xl font-extrabold tracking-tight text-slate-900 mt-3">{auditCount}</div>
                    <div className="text-[11px] text-slate-500 mt-1">generazioni</div>
                </div>
                <div className="hr-card shadow-xs p-5">
                    <div className="flex items-center justify-between">
                        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center">
                            <Layers size={16} className="text-slate-600" />
                        </div>
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">Report Portafoglio</div>
                    </div>
                    <div className="text-2xl font-extrabold tracking-tight text-slate-900 mt-3">{portfolioCount}</div>
                    <div className="text-[11px] text-slate-500 mt-1">generazioni</div>
                </div>
            </div>

            {/* History */}
            <div className="hr-card shadow-xs overflow-hidden" data-testid="history-table">
                <div className="px-6 py-4 border-b border-slate-200 bg-slate-50/80 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Clock size={14} className="text-slate-600" />
                        <h3 className="text-sm font-semibold text-slate-900">Storico Generazioni</h3>
                    </div>
                    <span className="text-[11px] text-slate-500 font-medium">{history.length} totali</span>
                </div>

                {loading && <div className="p-12 text-center"><Loader2 size={18} className="animate-spin mx-auto text-slate-400" /></div>}
                {error && <div className="p-4 bg-red-50 border-t border-red-200 text-red-700 text-sm flex items-start gap-2"><AlertCircle size={16} /> {error}</div>}

                {!loading && !error && (
                    history.length === 0 ? (
                        <div className="p-16 text-center">
                            <FileDown size={32} className="mx-auto text-slate-300 mb-3" />
                            <div className="text-sm text-slate-600 font-semibold mb-1">Nessun report generato</div>
                            <div className="text-xs text-slate-500">I report scaricati appariranno qui con la possibilità di rigenerarli con dati aggiornati.</div>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[800px]">
                                <thead>
                                    <tr className="bg-slate-50/80 border-b border-slate-200">
                                        <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500">Tipo</th>
                                        <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500">Cliente / Portafoglio</th>
                                        <th className="px-4 py-3 text-left text-[11px] uppercase tracking-wider font-semibold text-slate-500">Generato</th>
                                        <th className="px-4 py-3 text-center text-[11px] uppercase tracking-wider font-semibold text-slate-500">Score</th>
                                        <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider font-semibold text-slate-500">Petitum</th>
                                        <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider font-semibold text-slate-500">Fondo</th>
                                        <th className="px-4 py-3 text-right text-[11px] uppercase tracking-wider font-semibold text-slate-500">Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map((log) => {
                                        const isPortfolio = log.report_type === "portfolio_consolidato";
                                        const Icon = isPortfolio ? Layers : Building2;
                                        return (
                                            <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`history-row-${log.id}`}>
                                                <td className="px-4 py-3.5">
                                                    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${isPortfolio ? "bg-amber-100 text-amber-800" : "bg-blue-100 text-blue-800"}`}>
                                                        <Icon size={11} />
                                                        {isPortfolio ? "Portafoglio" : "Audit Cliente"}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3.5">
                                                    {log.client_id ? (
                                                        <Link to={`/admin/hr/clients/${log.client_id}`} className="text-sm font-semibold text-slate-900 hover:text-blue-600">
                                                            {log.client_name}
                                                        </Link>
                                                    ) : (
                                                        <span className="text-sm font-semibold text-slate-900">{log.client_name}</span>
                                                    )}
                                                    <div className="text-[10px] font-mono text-slate-400 mt-0.5 truncate max-w-xs">{log.filename}</div>
                                                </td>
                                                <td className="px-4 py-3.5 text-[11px] text-slate-600 font-mono">{fmtDateTime(log.generated_at)}</td>
                                                <td className="px-4 py-3.5 text-center">
                                                    {log.health_score !== null && log.health_score !== undefined ? (
                                                        <span className={`font-mono text-sm font-bold ${log.livello_rischio === "verde" ? "text-emerald-700" : log.livello_rischio === "giallo" ? "text-amber-700" : "text-red-700"}`}>
                                                            {log.health_score}
                                                        </span>
                                                    ) : <span className="text-slate-300 text-xs">—</span>}
                                                </td>
                                                <td className="px-4 py-3.5 text-right text-xs font-mono text-slate-700">{fmtEuro(log.petitum_totale)}</td>
                                                <td className="px-4 py-3.5 text-right text-xs font-mono text-amber-700 font-bold">{fmtEuro(log.fondo_rischi)}</td>
                                                <td className="px-4 py-3.5 text-right">
                                                    <button
                                                        onClick={() => handleRegenerateAudit(log)}
                                                        disabled={regeneratingId === log.id || generatingPortfolio}
                                                        className="hr-btn-ghost disabled:opacity-50"
                                                        data-testid={`btn-regen-${log.id}`}
                                                        title="Rigenera con dati aggiornati"
                                                    >
                                                        {regeneratingId === log.id ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                                                        Rigenera
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )
                )}
            </div>
        </HRLayout>
    );
}
